import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy – PaveMend';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = 'https://pavemend.co.uk/privacy-policy';
    return () => {
      document.title =
        'PaveMend – Driveways, Patios & Paving in South East London';
      if (canonical) canonical.href = 'https://pavemend.co.uk/';
    };
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-page__hero">
        <div className="container">
          <p className="section-eyebrow">Legal</p>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__meta">Last updated: August 2026</p>
        </div>
      </div>

      <div className="legal-page__body">
        <div className="container">
          <div className="legal-page__content">
            <section className="legal-section">
              <h2>1. Who We Are</h2>
              <p>
                PaveMend is the trading name of PaveMend Limited, a specialist
                paving and landscaping company registered in England and Wales
                (Company No. 17335800). Our registered address is 77 Maidstone
                Rd, Foots Cray, Sidcup, DA14 5HB. PaveMend Limited is the data
                controller for personal data collected through this website.
                References to "we", "us" or "our" in this policy mean PaveMend
                Limited.
              </p>
            </section>

            <section className="legal-section">
              <h2>2. What Information We Collect</h2>
              <p>We may collect the following personal information:</p>
              <ul>
                <li>
                  <strong>Contact details</strong> — name, phone number, and
                  email address submitted via our contact or booking form, or
                  given to us directly when you engage our services.
                </li>
                <li>
                  <strong>Property details</strong> — the address, city/town,
                  and postcode of the property where work is being requested,
                  and access notes relevant to carrying out the work (for
                  example gate codes, parking restrictions, or the presence of a
                  pet on site).
                </li>
                <li>
                  <strong>Project details</strong> — the type of service you're
                  enquiring about, your preferred visit date and time, any notes
                  or description you include, and details of any quotes, jobs,
                  bookings, or invoices associated with your enquiry.
                </li>
                <li>
                  <strong>Billing details</strong> — quote and invoice line
                  items, amounts, VAT, payment status, and due dates. We do not
                  collect or store card payment details; invoices are settled by
                  bank transfer or other means agreed directly with us.
                </li>
                <li>
                  <strong>Usage data</strong> — pages visited, time spent on
                  site, browser type, and device type, collected via cookies or
                  analytics tools (if enabled).
                </li>
              </ul>
              <p>
                Members of our staff who use our internal job management system
                have an account containing their name, email, phone number, and
                a securely hashed password, and their actions on jobs, quotes,
                invoices, and bookings are recorded in an audit log as described
                in section 6.
              </p>
            </section>

            <section className="legal-section">
              <h2>3. How We Use Your Information</h2>
              <p>We use the information you provide to:</p>
              <ul>
                <li>Respond to your enquiry or quote request.</li>
                <li>
                  Process and confirm bookings for a free initial visit to your
                  property, including scheduling and rearranging the date and
                  time.
                </li>
                <li>
                  Contact you to arrange a site visit or discuss your project.
                </li>
                <li>
                  Prepare and send quotes and invoices, schedule and manage
                  jobs, and keep records of work carried out at your property.
                </li>
                <li>
                  Send you relevant updates about your enquiry, booking, quote,
                  job, or invoice.
                </li>
                <li>Improve our website and services.</li>
              </ul>
              <p>
                We will never sell your personal data to third parties, use it
                for marketing, or use it for any purpose other than those listed
                above.
              </p>
            </section>

            <section className="legal-section">
              <h2>4. Legal Basis for Processing</h2>
              <p>
                We process your data on the basis of your consent (when you
                submit our contact or booking form), the performance of a
                contract (when you engage our services, confirm a booking, or we
                issue you a quote, job, or invoice), and our legitimate interest
                in responding to business enquiries and keeping accurate
                business and financial records. Where processing is based on
                consent, you can withdraw it at any time.
              </p>
            </section>

            <section className="legal-section">
              <h2>5. How Long We Keep Your Data</h2>
              <p>
                We retain enquiry data for up to 2 years after our last contact
                with you. Where an enquiry becomes a customer relationship — for
                example a confirmed quote, job, or invoice — we keep the related
                records for as long as necessary to meet our contractual,
                warranty, and legal obligations, including UK tax and accounting
                requirements that generally require financial records to be kept
                for 6 years. You may request deletion of your data at any time
                by emailing{' '}
                <a href="mailto:hello@pavemend.co.uk">hello@pavemend.co.uk</a>,
                though we may need to retain certain records where we have a
                legal obligation to do so.
              </p>
            </section>

            <section className="legal-section">
              <h2>6. Sharing Your Data and Internal Access</h2>
              <p>
                We do not sell your personal data. We share it only with service
                providers who help us run our business: our website and
                application hosting provider (Amazon Web Services), the email
                service we use to send and receive quotes, invoices, and
                enquiries, and analytics providers (only if you consent to
                analytics cookies). We may also disclose data where required by
                law.
              </p>
              <p>
                Your data relating to quotes, jobs, bookings, and invoices is
                accessible to our staff through our internal management system,
                strictly on a need-to-know basis to deliver our services to you.
                Access to this system requires a staff account, and actions
                taken on your records (such as status changes or edits) are
                logged in an audit trail for accountability and security
                purposes. This audit trail records what changed, when, and which
                staff member made the change — it is for our internal use and is
                not shared with third parties except where required by law.
              </p>
              <p>
                If we use third-party accounting or bookkeeping software to
                manage our financial records, your quote and invoice data may be
                shared with that provider for this purpose only.
              </p>
            </section>

            <section className="legal-section">
              <h2>7. Data Security</h2>
              <p>
                We use appropriate technical and organisational measures to
                protect your personal data against unauthorised access, loss,
                destruction, or alteration. These include encryption of data in
                transit (SSL/TLS), securely hashed staff passwords, and access
                to our internal management system restricted to staff accounts
                on a need-to-know basis.
              </p>
            </section>

            <section className="legal-section">
              <h2>8. Automated Decision-Making</h2>
              <p>
                We do not carry out any automated decision-making or profiling
                with your personal data. All quotes, pricing, and decisions
                about your enquiry or project are reviewed by a member of our
                team.
              </p>
            </section>

            <section className="legal-section">
              <h2>9. International Transfers</h2>
              <p>
                Our website and email systems are hosted in the UK and European
                Economic Area wherever possible. If a service provider processes
                your data outside the UK, we make sure appropriate safeguards
                are in place, such as UK adequacy regulations or standard
                contractual clauses approved for use in the UK.
              </p>
            </section>

            <section className="legal-section">
              <h2>10. Cookies</h2>
              <p>
                We use essential cookies to ensure the website functions
                correctly. With your consent, we may also set analytics cookies
                to understand how the site is used. You can accept or decline
                analytics cookies using our cookie banner — none are set unless
                you accept. No advertising or tracking cookies are used.
              </p>
            </section>

            <section className="legal-section">
              <h2>11. Your Rights</h2>
              <p>
                Under UK GDPR you have the right to access, correct, or delete
                your personal data, to object to or restrict how we process it,
                to receive a copy of your data in a portable format, and to
                withdraw consent at any time where processing is based on
                consent. To exercise any of these rights, email us at{' '}
                <a href="mailto:hello@pavemend.co.uk">hello@pavemend.co.uk</a>.
              </p>
              <p>
                If you are unhappy with how we handle your data, please contact
                us first so we can try to resolve it directly. If you remain
                unhappy, you also have the right to lodge a complaint with the
                Information Commissioner's Office (ICO) at{' '}
                <a href="https://ico.org.uk" target="_blank" rel="noreferrer">
                  ico.org.uk
                </a>{' '}
                or on 0303 123 1113.
              </p>
            </section>

            <section className="legal-section">
              <h2>12. Contact</h2>
              <p>
                If you have questions about this policy, wish to exercise your
                data rights, or have concerns about how we handle your personal
                data, contact us at{' '}
                <a href="mailto:hello@pavemend.co.uk">hello@pavemend.co.uk</a>,
                call <a href="tel:+447462911383">07462 911 383</a>, or write to
                us at Data Protection, PaveMend Limited, 77 Maidstone Rd, Foots
                Cray, Sidcup, DA14 5HB.
              </p>
            </section>

            <section className="legal-section">
              <h2>13. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect
                changes in our legal obligations or business practices. Any
                updates will be posted on this page with a revised "last
                updated" date above.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
